import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { PrizmTheme } from '../types/theme';
import { DOCUMENT } from '@angular/common';

export const ATT_THEME_KEY = 'data-prizm-theme';

@Injectable({
  providedIn: 'root',
})
export class PrizmThemeService implements OnDestroy {
  private rootElement_!: HTMLElement | null;
  public set rootElement(el: HTMLElement | null) {
    this.rootElement_ = el;
  }
  public get rootElement(): HTMLElement {
    return (this.rootElement_ as HTMLElement) ?? this.document.querySelector('body');
  }
  private readonly themeStorage = new Map<HTMLElement, string>();

  private readonly changeSource$ = new BehaviorSubject<{
    theme: PrizmTheme;
    el?: HTMLElement;
  }>({
    theme: this.getInitialTheme(),
  });
  readonly change$ = this.changeSource$.pipe(
    tap(data => data.el && this.themeStorage.set(data.el, data.theme))
  );
  public get value(): string {
    return this.changeSource$.value.theme;
  }
  readonly changesTheme$ = this.change$.pipe(map(i => i.theme));
  public readonly attThemeKey = 'data-prizm-theme';
  public readonly subscription = new Subscription();

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.subscription.add(this.change$.pipe(tap(theme => this.setToHtml(theme.theme, theme.el))).subscribe());
  }

  public getLastThemeForElement(el: HTMLElement = this.rootElement): string | null {
    let theme = this.themeStorage.get(el);
    if (el !== this.rootElement_)
      theme = el.closest(`[${ATT_THEME_KEY}]`)?.getAttribute(ATT_THEME_KEY) as string;
    return theme ?? null;
  }

  public getLastThemeForElement$(el: HTMLElement = this.rootElement): Observable<string | null> {
    return this.change$.pipe(
      map(() => this.getLastThemeForElement(el)),
      distinctUntilChanged()
    );
  }

  public getInvertedThemeByElement$(
    element = this.rootElement,
    pairThemeValues: Record<string, string> = {
      light: 'dark',
      dark: 'light',
    }
  ): Observable<string | null> {
    return this.getLastThemeForElement$(element).pipe(map(theme => theme && pairThemeValues[theme]));
  }

  public getByElement(el?: HTMLElement): PrizmTheme {
    return (el ?? this.rootElement)?.getAttribute(ATT_THEME_KEY) as PrizmTheme;
  }

  private setToHtml(theme: PrizmTheme, el?: HTMLElement): void {
    (el ?? this.rootElement)?.setAttribute(ATT_THEME_KEY, theme);
  }

  public update(theme: PrizmTheme, el: HTMLElement = this.rootElement): void {
    this.changeSource$.next({
      theme,
      el,
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public getTokenValue(token: string, el: HTMLElement | null = null): string | null {
    el = el ?? this.rootElement;
    if (!el) return null;
    const style = getComputedStyle(el);
    return style.getPropertyValue(`--${token}`);
  }

  public updateTokenValue(token: string, value: string, el: HTMLElement | null = null): boolean {
    el = el ?? this.rootElement;
    if (!el) return false;
    const style = el.style;
    style.setProperty(`--${token}`, value);
    return true;
  }

  // Need for microfront
  private getInitialTheme(): PrizmTheme {
    return this.rootElement.getAttribute(ATT_THEME_KEY) ?? 'light';
  }
}
